import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { analyticsAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels?: string[];
  datasets: Array<{
    label: string;
    data: number[] | Array<{ x: number; y: number; r: number }>;
    borderColor?: string;
    backgroundColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointBorderWidth?: number;
    pointRadius?: number;
    borderRadius?: number;
    borderSkipped?: boolean;
  }>;
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'recommendation' | 'warning';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface AnalyticsState {
  engagement: ChartData | null;
  platform: ChartData | null;
  bubble: ChartData | null;
  platformSpecific: {
    instagram: ChartData | null;
    facebook: ChartData | null;
    twitter: ChartData | null;
    linkedin: ChartData | null;
    tiktok: ChartData | null;
  };
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    engagement: boolean;
    platform: boolean;
    bubble: boolean;
    platformSpecific: {
      instagram: boolean;
      facebook: boolean;
      twitter: boolean;
      linkedin: boolean;
      tiktok: boolean;
    };
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    engagement: string | null;
    platform: string | null;
    bubble: string | null;
    platformSpecific: {
      instagram: string | null;
      facebook: string | null;
      twitter: string | null;
      linkedin: string | null;
      tiktok: string | null;
    };
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

const normalizeBubbleData = (bubbleData: any): ChartData => {
  if (!bubbleData || !Array.isArray(bubbleData.datasets) || bubbleData.datasets.length === 0) {
    return {
      labels: bubbleData?.labels ?? [],
      datasets: bubbleData?.datasets ?? [],
    };
  }

  const allRadii = bubbleData.datasets.flatMap((dataset: any) => {
    if (!Array.isArray(dataset.data)) return [];
    return dataset.data
      .map((point: any) => (point && typeof point.r === 'number' ? point.r : null))
      .filter((value: number | null): value is number => value !== null && Number.isFinite(value));
  });

  if (allRadii.length === 0) {
    return {
      labels: bubbleData.labels ?? [],
      datasets: bubbleData.datasets,
    };
  }

  const minRadius = Math.min(...allRadii);
  const maxRadius = Math.max(...allRadii);
  const MIN_PIXEL_RADIUS = 10;
  const MAX_PIXEL_RADIUS = 45;

  const scaleRadius = (value: number) => {
    if (!Number.isFinite(value)) return MIN_PIXEL_RADIUS;
    if (maxRadius === minRadius) {
      return (MIN_PIXEL_RADIUS + MAX_PIXEL_RADIUS) / 2;
    }
    const normalized = (value - minRadius) / (maxRadius - minRadius);
    return MIN_PIXEL_RADIUS + normalized * (MAX_PIXEL_RADIUS - MIN_PIXEL_RADIUS);
  };

  const datasets = bubbleData.datasets.map((dataset: any) => {
    if (!Array.isArray(dataset.data)) return dataset;
    const normalizedData = dataset.data.map((point: any) => {
      if (!point || typeof point !== 'object') return point;
      const originalRadius = typeof point.r === 'number' ? point.r : Number(point.r);
      return {
        ...point,
        rawR: originalRadius,
        r: scaleRadius(originalRadius),
      };
    });

    return {
      ...dataset,
      data: normalizedData,
    };
  });

  return {
    labels: bubbleData.labels ?? [],
    datasets,
  };
};

// Action Types
export type AnalyticsAction =
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_PLATFORM_START' }
  | { type: 'FETCH_PLATFORM_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_PLATFORM_ERROR'; payload: string }
  | { type: 'FETCH_BUBBLE_START' }
  | { type: 'FETCH_BUBBLE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_BUBBLE_ERROR'; payload: string }
  | { type: 'FETCH_PLATFORM_SPECIFIC_START'; payload: string }
  | { type: 'FETCH_PLATFORM_SPECIFIC_SUCCESS'; payload: { platform: string; data: ChartData } }
  | { type: 'FETCH_PLATFORM_SPECIFIC_ERROR'; payload: { platform: string; error: string } }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

// Initial State
const initialState: AnalyticsState = {
  engagement: null,
  platform: null,
  bubble: null,
  platformSpecific: {
    instagram: null,
    facebook: null,
    twitter: null,
    linkedin: null,
    tiktok: null,
  },
  aiAnalysis: {},
  loading: {
    engagement: false,
    platform: false,
    bubble: false,
    platformSpecific: {
      instagram: false,
      facebook: false,
      twitter: false,
      linkedin: false,
      tiktok: false,
    },
    aiAnalysis: {},
  },
  error: {
    engagement: null,
    platform: null,
    bubble: null,
    platformSpecific: {
      instagram: null,
      facebook: null,
      twitter: null,
      linkedin: null,
      tiktok: null,
    },
    aiAnalysis: {},
  },
};

// Reducer
const analyticsReducer = (state: AnalyticsState, action: AnalyticsAction): AnalyticsState => {
  switch (action.type) {
    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };

    case 'FETCH_PLATFORM_START':
      return {
        ...state,
        loading: { ...state.loading, platform: true },
        error: { ...state.error, platform: null },
      };
    case 'FETCH_PLATFORM_SUCCESS':
      return {
        ...state,
        platform: action.payload,
        loading: { ...state.loading, platform: false },
        error: { ...state.error, platform: null },
      };
    case 'FETCH_PLATFORM_ERROR':
      return {
        ...state,
        loading: { ...state.loading, platform: false },
        error: { ...state.error, platform: action.payload },
      };

    case 'FETCH_BUBBLE_START':
      return {
        ...state,
        loading: { ...state.loading, bubble: true },
        error: { ...state.error, bubble: null },
      };
    case 'FETCH_BUBBLE_SUCCESS':
      return {
        ...state,
        bubble: action.payload,
        loading: { ...state.loading, bubble: false },
        error: { ...state.error, bubble: null },
      };
    case 'FETCH_BUBBLE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, bubble: false },
        error: { ...state.error, bubble: action.payload },
      };

    case 'FETCH_PLATFORM_SPECIFIC_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          platformSpecific: {
            ...state.loading.platformSpecific,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          platformSpecific: {
            ...state.error.platformSpecific,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_PLATFORM_SPECIFIC_SUCCESS':
      return {
        ...state,
        platformSpecific: {
          ...state.platformSpecific,
          [action.payload.platform]: action.payload.data,
        },
        loading: {
          ...state.loading,
          platformSpecific: {
            ...state.loading.platformSpecific,
            [action.payload.platform]: false,
          },
        },
        error: {
          ...state.error,
          platformSpecific: {
            ...state.error.platformSpecific,
            [action.payload.platform]: null,
          },
        },
      };
    case 'FETCH_PLATFORM_SPECIFIC_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          platformSpecific: {
            ...state.loading.platformSpecific,
            [action.payload.platform]: false,
          },
        },
        error: {
          ...state.error,
          platformSpecific: {
            ...state.error.platformSpecific,
            [action.payload.platform]: action.payload.error,
          },
        },
      };

    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };

    default:
      return state;
  }
};

// Context
interface AnalyticsContextType {
  state: AnalyticsState;
  dispatch: React.Dispatch<AnalyticsAction>;
  fetchEngagementData: () => Promise<void>;
  fetchPlatformData: () => Promise<void>;
  fetchBubbleData: () => Promise<void>;
  fetchPlatformSpecificData: (platform: string) => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
  fetchAllAnalyticsData: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

// Provider
interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(analyticsReducer, initialState);
  const { business } = useAuth();

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_ENGAGEMENT_START' });
      const response = await analyticsAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      // 404 hatası için özel mesaj
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_ENGAGEMENT_ERROR', 
          payload: 'PREPARING' // Özel durum
        });
      } else {
        dispatch({ 
          type: 'FETCH_ENGAGEMENT_ERROR', 
          payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
        });
      }
    }
  }, [business?._id]);

  const fetchPlatformData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_PLATFORM_START' });
      const response = await analyticsAPI.getPlatformData(business._id);
      dispatch({ type: 'FETCH_PLATFORM_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_PLATFORM_ERROR', 
          payload: 'PREPARING'
        });
      } else {
        dispatch({ 
          type: 'FETCH_PLATFORM_ERROR', 
          payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
        });
      }
    }
  }, [business?._id]);

  const fetchBubbleData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_BUBBLE_START' });
      const response = await analyticsAPI.getBubbleData(business._id);
      const normalizedBubble = normalizeBubbleData(response.data.data);
      dispatch({ type: 'FETCH_BUBBLE_SUCCESS', payload: normalizedBubble });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_BUBBLE_ERROR', 
          payload: 'PREPARING'
        });
      } else {
        dispatch({ 
          type: 'FETCH_BUBBLE_ERROR', 
          payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
        });
      }
    }
  }, [business?._id]);

  const fetchPlatformSpecificData = useCallback(async (platform: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: platform });
      const response = await analyticsAPI.getPlatformSpecificData(business._id, platform);
      dispatch({ 
        type: 'FETCH_PLATFORM_SPECIFIC_SUCCESS', 
        payload: { platform, data: response.data.data } 
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_PLATFORM_SPECIFIC_ERROR', 
          payload: { 
            platform, 
            error: 'PREPARING'
          } 
        });
      } else {
        dispatch({ 
          type: 'FETCH_PLATFORM_SPECIFIC_ERROR', 
          payload: { 
            platform, 
            error: error instanceof Error ? error.message : 'Bilinmeyen hata' 
          } 
        });
      }
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
      const response = await analyticsAPI.getAIAnalysis(business._id, chartType);
      console.log('response', response);
      
      dispatch({ 
        type: 'FETCH_AI_ANALYSIS_SUCCESS', 
        payload: { chartType, data: response.data.data } 
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_AI_ANALYSIS_ERROR', 
          payload: { 
            chartType, 
            error: 'PREPARING'
          } 
        });
      } else {
        dispatch({ 
          type: 'FETCH_AI_ANALYSIS_ERROR', 
          payload: { 
            chartType, 
            error: error instanceof Error ? error.message : 'Bilinmeyen hata' 
          } 
        });
      }
    }
  }, [business?._id]);

  // Tüm analytics verisini tek seferde çekip state'e dağıtan fonksiyon
  const fetchAllAnalyticsData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      // Tüm loading state'lerini başlat
      dispatch({ type: 'FETCH_ENGAGEMENT_START' });
      dispatch({ type: 'FETCH_BUBBLE_START' });
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: 'instagram' });
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: 'facebook' });
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: 'twitter' });
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: 'linkedin' });
      dispatch({ type: 'FETCH_PLATFORM_SPECIFIC_START', payload: 'tiktok' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'engagement' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'bubble' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'instagram' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'facebook' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'twitter' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'linkedin' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'tiktok' });

      const response = await analyticsAPI.getAllAnalyticsData(business._id, 'analytics');
      // API'den gelen data bir array, ilk elemanı alıyoruz
      const dataArray = response.data.data;
      const analyticsData = Array.isArray(dataArray) && dataArray.length > 0 ? dataArray[0] : dataArray;

      // Engagement data - data.engagement içinde labels ve datasets var
      if (analyticsData?.data?.engagement) {
        dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: analyticsData.data.engagement });
      } else {
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'PREPARING' });
      }

      // Bubble data - data.bubble içinde datasets var
      if (analyticsData?.data?.bubble) {
        const normalizedBubble = normalizeBubbleData(analyticsData.data.bubble);
        dispatch({ type: 'FETCH_BUBBLE_SUCCESS', payload: normalizedBubble });
      } else {
        dispatch({ type: 'FETCH_BUBBLE_ERROR', payload: 'PREPARING' });
      }

      // Platform specific data - data.platform.{platform} içinde labels ve datasets var
      const platforms = ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'];
      platforms.forEach(platform => {
        if (analyticsData?.data?.platform?.[platform]) {
          dispatch({ 
            type: 'FETCH_PLATFORM_SPECIFIC_SUCCESS', 
            payload: { platform, data: analyticsData.data.platform[platform] } 
          });
        } else {
          dispatch({ 
            type: 'FETCH_PLATFORM_SPECIFIC_ERROR', 
            payload: { platform, error: 'PREPARING' } 
          });
        }
      });

      // AI Analysis - data.aiAnalysis.{chartType} içinde insights var
      const analysisTypes = ['engagement', 'bubble', 'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'];
      analysisTypes.forEach(chartType => {
        if (analyticsData?.data?.aiAnalysis?.[chartType]) {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_SUCCESS', 
            payload: { chartType, data: analyticsData.data.aiAnalysis[chartType] } 
          });
        } else {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: 'PREPARING' } 
          });
        }
      });

    } catch (error: any) {
      // Hata durumunda tüm state'leri PREPARING olarak işaretle
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_BUBBLE_ERROR', payload: 'PREPARING' });
        ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'].forEach(platform => {
          dispatch({ 
            type: 'FETCH_PLATFORM_SPECIFIC_ERROR', 
            payload: { platform, error: 'PREPARING' } 
          });
        });
        ['engagement', 'bubble', 'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: 'PREPARING' } 
          });
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_BUBBLE_ERROR', payload: errorMessage });
        ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'].forEach(platform => {
          dispatch({ 
            type: 'FETCH_PLATFORM_SPECIFIC_ERROR', 
            payload: { platform, error: errorMessage } 
          });
        });
        ['engagement', 'bubble', 'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: errorMessage } 
          });
        });
      }
    }
  }, [business?._id]);

  const value: AnalyticsContextType = {
    state,
    dispatch,
    fetchEngagementData,
    fetchPlatformData,
    fetchBubbleData,
    fetchPlatformSpecificData,
    fetchAIAnalysis,
    fetchAllAnalyticsData,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
